<script lang="ts">
	import { onMount, onDestroy } from "svelte";
	import { yyyy, zzzz } from "../../../store.svelte";
	import { toast } from "../../../lib/toast.svelte";
	import { slide } from "svelte/transition";
	import Fa from "svelte-fa";
	import {
		faTimes,
		faRotateRight,
		faCheck,
		faTable,
		faChevronDown,
		faChevronRight,
		faRuler,
		faBolt,
		faSitemap,
		faDownload,
	} from "@fortawesome/free-solid-svg-icons";

	const url: string = yyyy.uri.api;

	let loading = $state(false);
	let hasPolygon = $state(false);
	let hasResults = $state(false);
	let pointCount = $state(0);
	let contextData: any[] = $state([]);
	let polygonGeojson: any = $state(null);
	let expanded = $state<Record<string, boolean>>({});

	type FeederSummary = {
		nodes: Record<string, { total: number; phases: Record<string, number> }>;
		edges: Record<string, { totalLength: number; byPhase: Record<string, number> }>;
		totalLength: number;
		kva: { byPhase: { A: number; B: number; C: number }; totalKVA: number };
	};

	function buildSummary(items: any[]): FeederSummary {
		const nodes: FeederSummary["nodes"] = {};
		const edges: FeederSummary["edges"] = {};
		let totalLength = 0;
		const kva = { byPhase: { A: 0, B: 0, C: 0 }, totalKVA: 0 };

		for (const d of items) {
			const table = d.lw_table || "Unknown";
			const phase = d.phase || "Unknown";

			if (d.length_geom !== undefined) {
				// Edge
				const len = d.length_geom || 0;
				if (!edges[table]) edges[table] = { totalLength: 0, byPhase: {} };
				edges[table].totalLength += len;
				edges[table].byPhase[phase] = (edges[table].byPhase[phase] || 0) + len;
				totalLength += len;
			} else {
				// Node
				if (!nodes[table]) nodes[table] = { total: 0, phases: {} };
				nodes[table].total++;
				nodes[table].phases[phase] = (nodes[table].phases[phase] || 0) + 1;

				if (d.voltamps) {
					if (d.voltamps[0] && !isNaN(d.voltamps[0])) { const v = d.voltamps[0] / 1000; kva.byPhase.A += v; kva.totalKVA += v; }
					if (d.voltamps[1] && !isNaN(d.voltamps[1])) { const v = d.voltamps[1] / 1000; kva.byPhase.B += v; kva.totalKVA += v; }
					if (d.voltamps[2] && !isNaN(d.voltamps[2])) { const v = d.voltamps[2] / 1000; kva.byPhase.C += v; kva.totalKVA += v; }
				}
			}
		}

		return { nodes, edges, totalLength, kva };
	}

	// Group by feederid
	let byFeeder = $derived.by(() => {
		const groups: Record<string, any[]> = {};
		for (const d of contextData) {
			const fid = d.feederid || "Unknown";
			if (!groups[fid]) groups[fid] = [];
			groups[fid].push(d);
		}
		const result: Record<string, FeederSummary> = {};
		for (const [fid, items] of Object.entries(groups)) {
			result[fid] = buildSummary(items);
		}
		return result;
	});

	let feederIds = $derived(Object.keys(byFeeder).sort());

	// Total across all feeders
	let totalSummary = $derived(buildSummary(contextData));

	function formatLength(length: number) {
		if (length >= 1000) return `${(length / 1000).toFixed(2)} km`;
		if (length >= 1) return `${length.toFixed(2)} m`;
		return `${(length * 100).toFixed(2)} cm`;
	}

	function formatKVA(kva: number) {
		if (kva >= 1000) return `${(kva / 1000).toFixed(2)} MVA`;
		return `${kva.toFixed(2)} kVA`;
	}

	function toggle(key: string) {
		expanded[key] = !expanded[key];
		expanded = { ...expanded };
	}

	const handlePolygonComplete = async (geojson: any) => {
		hasPolygon = true;
		loading = true;
		polygonGeojson = geojson;

		try {
			const response = await fetch(`${url}/rpc/context`, {
				method: "post",
				headers: {
					"Content-Type": "application/json",
					Authorization: "Bearer " + yyyy.sesh.logtoken.token,
				},
				body: JSON.stringify({
					livewire: yyyy.sesh.activeLivewire.wireident,
					geom: JSON.stringify(geojson),
				}),
			});

			if (!response.ok) throw new Error(`Error: ${response.statusText}`);

			const result = await response.json();
			contextData = result.data || [];
			hasResults = contextData.length > 0;
		} catch (err) {
			console.error("Context query failed:", err);
			toast.error("Failed to query context", 3000, "top-center", "vertical");
		} finally {
			loading = false;
		}
	};

	const handlePolygonPoint = () => {
		pointCount++;
	};

	const redraw = () => {
		hasPolygon = false;
		hasResults = false;
		pointCount = 0;
		contextData = [];
		polygonGeojson = null;
		zzzz.emit("clearContextPolygon");
		zzzz.emit("startContextDraw");
	};

	const close = () => {
		yyyy.ctrl.control = undefined;
		yyyy.ctrl.expanded = false;
	};

	const exportReport = () => {
		const lines: string[] = [];
		const ts = new Date().toLocaleString();
		const livewire = yyyy.sesh.activeLivewire?.livewire || "Unknown";

		lines.push("Spotlight Report");
		lines.push(`Livewire: ${livewire}`);
		lines.push(`Generated: ${ts}`);
		lines.push(`Total Features: ${contextData.length}`);
		lines.push("=".repeat(50));

		for (const fid of feederIds) {
			const fs = byFeeder[fid];
			lines.push("");
			lines.push(`Feeder: ${fid}`);
			lines.push("-".repeat(40));

			for (const [table, data] of Object.entries(fs.nodes)) {
				lines.push(`  ${table}: ${data.total}`);
				for (const [phase, count] of Object.entries(data.phases)) {
					lines.push(`    Phase ${phase}: ${count}`);
				}
			}

			for (const [table, data] of Object.entries(fs.edges)) {
				lines.push(`  ${table}: ${formatLength(data.totalLength)}`);
				for (const [phase, len] of Object.entries(data.byPhase)) {
					lines.push(`    Phase ${phase}: ${formatLength(len)} (${((len / data.totalLength) * 100).toFixed(1)}%)`);
				}
			}

			if (fs.kva.totalKVA > 0) {
				lines.push(`  Power Capacity: ${formatKVA(fs.kva.totalKVA)}`);
				if (fs.kva.byPhase.A > 0) lines.push(`    Phase A: ${formatKVA(fs.kva.byPhase.A)}`);
				if (fs.kva.byPhase.B > 0) lines.push(`    Phase B: ${formatKVA(fs.kva.byPhase.B)}`);
				if (fs.kva.byPhase.C > 0) lines.push(`    Phase C: ${formatKVA(fs.kva.byPhase.C)}`);
			}
		}

		if (feederIds.length > 1) {
			lines.push("");
			lines.push("TOTAL");
			lines.push("=".repeat(50));

			for (const [table, data] of Object.entries(totalSummary.nodes)) {
				lines.push(`  ${table}: ${data.total}`);
			}
			for (const [table, data] of Object.entries(totalSummary.edges)) {
				lines.push(`  ${table}: ${formatLength(data.totalLength)}`);
			}
			if (totalSummary.totalLength > 0) {
				lines.push(`  Total Length: ${formatLength(totalSummary.totalLength)}`);
			}
			if (totalSummary.kva.totalKVA > 0) {
				lines.push(`  Total Power Capacity: ${formatKVA(totalSummary.kva.totalKVA)}`);
			}
		}

		if (polygonGeojson) {
			lines.push("");
			lines.push("POLYGON (GeoJSON)");
			lines.push("=".repeat(50));
			lines.push(JSON.stringify(polygonGeojson, null, 2));
		}

		const blob = new Blob([lines.join("\n")], { type: "text/plain" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `spotlight-${new Date().toISOString().slice(0, 10)}.txt`;
		a.click();
		URL.revokeObjectURL(url);
	};

	let zoomedIn = $derived(yyyy.map.location?.z > 15);

	$effect(() => {
		if (zoomedIn && !hasPolygon) {
			zzzz.emit("startContextDraw");
		} else if (!zoomedIn && !hasPolygon) {
			zzzz.emit("endContextDraw");
		}
	});

	onMount(() => {
		if (zoomedIn) zzzz.emit("startContextDraw");
		zzzz.on("contextPolygonComplete", handlePolygonComplete);
		zzzz.on("contextPolygonPoint", handlePolygonPoint);
	});

	onDestroy(() => {
		zzzz.emit("endContextDraw");
		zzzz.off("contextPolygonComplete", handlePolygonComplete);
		zzzz.off("contextPolygonPoint", handlePolygonPoint);
	});
</script>

<div id="spotlight">
	<div class="panel-header">
		<button class="close-btn" onclick={close}>
			<Fa icon={faTimes} size="1.75x" />
		</button>
		<span class="panel-title">Spotlight</span>
	</div>

	{#if !zoomedIn && !hasPolygon}
		<div class="draw-prompt">
			<p class="prompt-text">Zoom in more to use Spotlight</p>
			<p class="helper-text">Spotlight lets you select an area on the map to get a summary of all network features within it, broken down by feeder, phase, and asset type.</p>
		</div>
	{:else if !hasPolygon}
		<div class="draw-prompt">
			<p class="prompt-text">Click on the map to draw a polygon</p>
			<p class="helper-text">Place points to outline the area you want to inspect. Use at least 3 points, then click Finish Drawing to query the network.</p>
			{#if pointCount > 0}
				<p class="point-count">{pointCount} point{pointCount !== 1 ? 's' : ''} placed</p>
			{/if}
			{#if pointCount >= 3}
				<button class="finish-btn" onclick={() => zzzz.emit("finishContextDraw")}>
					<Fa icon={faCheck} size="0.9x" />
					Finish Drawing
				</button>
			{/if}
		</div>
	{:else if loading}
		<div class="loading-container">
			<div class="spinner"></div>
			<p class="loading-text">Querying context...</p>
		</div>
	{:else if hasResults}
		<div class="summary-container">
			<div class="summary-header">
				<h3>Context Summary</h3>
				<span class="total-count">{contextData.length} features</span>
			</div>
			<div class="summary-content">
				{#each feederIds as fid}
					{@const fs = byFeeder[fid]}
					{@const fKey = `feeder-${fid}`}
					<div class="feeder-group">
						<div class="feeder-header" onclick={() => toggle(fKey)}>
							<span class="expand-icon">
								<Fa icon={expanded[fKey] ? faChevronDown : faChevronRight} />
							</span>
							<span class="table-icon"><Fa icon={faSitemap} /></span>
							<span class="table-name">Feeder {fid}</span>
						</div>

						{#if expanded[fKey]}
							<div class="feeder-content" transition:slide={{ duration: 200 }}>
								{#each Object.entries(fs.nodes) as [tableName, data]}
									<div class="table-item">
										<div class="table-header nested" onclick={() => toggle(`${fKey}-${tableName}`)}>
											<span class="expand-icon">
												<Fa icon={expanded[`${fKey}-${tableName}`] ? faChevronDown : faChevronRight} />
											</span>
											<span class="table-icon"><Fa icon={faTable} /></span>
											<span class="table-name">{tableName}</span>
											<span class="table-count">{data.total}</span>
										</div>
										{#if expanded[`${fKey}-${tableName}`]}
											<div class="phase-list" transition:slide={{ duration: 200 }}>
												{#each Object.entries(data.phases) as [phaseName, count]}
													<div class="phase-item">
														<span class="phase-name phase-indicator phase-{phaseName.toLowerCase()}">{phaseName}</span>
														<span class="phase-count">{count}</span>
													</div>
												{/each}
											</div>
										{/if}
									</div>
								{/each}

								{#each Object.entries(fs.edges) as [tableName, tableData]}
									<div class="table-item">
										<div class="table-header nested" onclick={() => toggle(`${fKey}-${tableName}-len`)}>
											<span class="expand-icon">
												<Fa icon={expanded[`${fKey}-${tableName}-len`] ? faChevronDown : faChevronRight} />
											</span>
											<span class="table-icon"><Fa icon={faRuler} /></span>
											<span class="table-name">{tableName}</span>
											<span class="kva-badge">{formatLength(tableData.totalLength)}</span>
										</div>
										{#if expanded[`${fKey}-${tableName}-len`]}
											<div class="phase-list" transition:slide={{ duration: 200 }}>
												{#each Object.entries(tableData.byPhase) as [phaseName, len]}
													<div class="phase-item">
														<span class="phase-name phase-indicator phase-{phaseName.toLowerCase()}">{phaseName}</span>
														<div class="phase-kva-details">
															<span class="kva-value">{formatLength(len)}</span>
															<span class="kva-percent">({((len / tableData.totalLength) * 100).toFixed(1)}%)</span>
														</div>
													</div>
												{/each}
											</div>
										{/if}
									</div>
								{/each}

								{#if fs.kva.totalKVA > 0}
									<div class="table-item">
										<div class="table-header nested" onclick={() => toggle(`${fKey}-kva`)}>
											<span class="expand-icon">
												<Fa icon={expanded[`${fKey}-kva`] ? faChevronDown : faChevronRight} />
											</span>
											<span class="table-icon"><Fa icon={faBolt} /></span>
											<span class="table-name">Power Capacity</span>
											<span class="kva-badge">{formatKVA(fs.kva.totalKVA)}</span>
										</div>
										{#if expanded[`${fKey}-kva`]}
											<div class="phase-list" transition:slide={{ duration: 200 }}>
												{#if fs.kva.byPhase.A > 0}
													<div class="phase-item">
														<span class="phase-name phase-indicator phase-a">A</span>
														<div class="phase-kva-details">
															<span class="kva-value">{formatKVA(fs.kva.byPhase.A)}</span>
															<span class="kva-percent">({((fs.kva.byPhase.A / fs.kva.totalKVA) * 100).toFixed(1)}%)</span>
														</div>
													</div>
												{/if}
												{#if fs.kva.byPhase.B > 0}
													<div class="phase-item">
														<span class="phase-name phase-indicator phase-b">B</span>
														<div class="phase-kva-details">
															<span class="kva-value">{formatKVA(fs.kva.byPhase.B)}</span>
															<span class="kva-percent">({((fs.kva.byPhase.B / fs.kva.totalKVA) * 100).toFixed(1)}%)</span>
														</div>
													</div>
												{/if}
												{#if fs.kva.byPhase.C > 0}
													<div class="phase-item">
														<span class="phase-name phase-indicator phase-c">C</span>
														<div class="phase-kva-details">
															<span class="kva-value">{formatKVA(fs.kva.byPhase.C)}</span>
															<span class="kva-percent">({((fs.kva.byPhase.C / fs.kva.totalKVA) * 100).toFixed(1)}%)</span>
														</div>
													</div>
												{/if}
											</div>
										{/if}
									</div>
								{/if}
							</div>
						{/if}
					</div>
				{/each}

				<!-- Total across all feeders -->
				{#if feederIds.length > 1}
					<div class="totals-section">
						<div class="totals-label">Total</div>

						{#each Object.entries(totalSummary.nodes) as [tableName, data]}
							<div class="table-item">
								<div class="table-header" onclick={() => toggle(`total-${tableName}`)}>
									<span class="expand-icon">
										<Fa icon={expanded[`total-${tableName}`] ? faChevronDown : faChevronRight} />
									</span>
									<span class="table-icon"><Fa icon={faTable} /></span>
									<span class="table-name">{tableName}</span>
									<span class="table-count">{data.total}</span>
								</div>
								{#if expanded[`total-${tableName}`]}
									<div class="phase-list" transition:slide={{ duration: 200 }}>
										{#each Object.entries(data.phases) as [phaseName, count]}
											<div class="phase-item">
												<span class="phase-name phase-indicator phase-{phaseName.toLowerCase()}">{phaseName}</span>
												<span class="phase-count">{count}</span>
											</div>
										{/each}
									</div>
								{/if}
							</div>
						{/each}

						{#each Object.entries(totalSummary.edges) as [tableName, tableData]}
							<div class="table-item">
								<div class="table-header" onclick={() => toggle(`total-${tableName}-len`)}>
									<span class="expand-icon">
										<Fa icon={expanded[`total-${tableName}-len`] ? faChevronDown : faChevronRight} />
									</span>
									<span class="table-icon"><Fa icon={faRuler} /></span>
									<span class="table-name">{tableName}</span>
									<span class="kva-badge total-badge">{formatLength(tableData.totalLength)}</span>
								</div>
								{#if expanded[`total-${tableName}-len`]}
									<div class="phase-list" transition:slide={{ duration: 200 }}>
										{#each Object.entries(tableData.byPhase) as [phaseName, len]}
											<div class="phase-item">
												<span class="phase-name phase-indicator phase-{phaseName.toLowerCase()}">{phaseName}</span>
												<div class="phase-kva-details">
													<span class="kva-value">{formatLength(len)}</span>
													<span class="kva-percent">({((len / tableData.totalLength) * 100).toFixed(1)}%)</span>
												</div>
											</div>
										{/each}
									</div>
								{/if}
							</div>
						{/each}

						{#if totalSummary.totalLength > 0}
							<div class="table-item">
								<div class="table-header">
									<span class="expand-icon" style="visibility:hidden"><Fa icon={faChevronRight} /></span>
									<span class="table-icon"><Fa icon={faRuler} /></span>
									<span class="table-name">Total Length</span>
									<span class="kva-badge total-badge">{formatLength(totalSummary.totalLength)}</span>
								</div>
							</div>
						{/if}

						{#if totalSummary.kva.totalKVA > 0}
							<div class="table-item">
								<div class="table-header" onclick={() => toggle('total-kva')}>
									<span class="expand-icon">
										<Fa icon={expanded['total-kva'] ? faChevronDown : faChevronRight} />
									</span>
									<span class="table-icon"><Fa icon={faBolt} /></span>
									<span class="table-name">Total Power Capacity</span>
									<span class="kva-badge total-badge">{formatKVA(totalSummary.kva.totalKVA)}</span>
								</div>
								{#if expanded['total-kva']}
									<div class="phase-list" transition:slide={{ duration: 200 }}>
										{#if totalSummary.kva.byPhase.A > 0}
											<div class="phase-item">
												<span class="phase-name phase-indicator phase-a">A</span>
												<div class="phase-kva-details">
													<span class="kva-value">{formatKVA(totalSummary.kva.byPhase.A)}</span>
													<span class="kva-percent">({((totalSummary.kva.byPhase.A / totalSummary.kva.totalKVA) * 100).toFixed(1)}%)</span>
												</div>
											</div>
										{/if}
										{#if totalSummary.kva.byPhase.B > 0}
											<div class="phase-item">
												<span class="phase-name phase-indicator phase-b">B</span>
												<div class="phase-kva-details">
													<span class="kva-value">{formatKVA(totalSummary.kva.byPhase.B)}</span>
													<span class="kva-percent">({((totalSummary.kva.byPhase.B / totalSummary.kva.totalKVA) * 100).toFixed(1)}%)</span>
												</div>
											</div>
										{/if}
										{#if totalSummary.kva.byPhase.C > 0}
											<div class="phase-item">
												<span class="phase-name phase-indicator phase-c">C</span>
												<div class="phase-kva-details">
													<span class="kva-value">{formatKVA(totalSummary.kva.byPhase.C)}</span>
													<span class="kva-percent">({((totalSummary.kva.byPhase.C / totalSummary.kva.totalKVA) * 100).toFixed(1)}%)</span>
												</div>
											</div>
										{/if}
									</div>
								{/if}
							</div>
						{/if}
					</div>
				{/if}

				{#if contextData.length === 0}
					<div class="empty-state">No data available</div>
				{/if}
			</div>
		</div>
		<div class="button-group">
			<button class="option-button" onclick={exportReport}>
				<Fa icon={faDownload} size="0.9x" />
				Export
			</button>
			<button class="option-button" onclick={redraw}>
				<Fa icon={faRotateRight} size="0.9x" />
				Redraw
			</button>
		</div>
	{:else}
		<div class="draw-prompt">
			<p class="prompt-text">No results found in the selected area</p>
			<div class="button-group" style="margin-top: 1rem;">
				<button class="option-button" onclick={redraw}>
					<Fa icon={faRotateRight} size="0.9x" />
					Redraw
				</button>
			</div>
		</div>
	{/if}
</div>

<style>
	#spotlight {
		width: 100%;
		padding: 8.775px;
	}

	.panel-header {
		display: flex;
		align-items: center;
		min-height: 45px;
		gap: 0.5rem;
	}

	.close-btn {
		background: none;
		border: none;
		cursor: pointer;
		color: #ff3e00;
		padding: 0;
	}

	.close-btn:hover {
		opacity: 0.7;
	}

	.panel-title {
		font-weight: bolder;
		padding-top: 2px;
		flex: 1;
	}

	.button-group {
		display: flex;
		margin-top: 0.75rem;
	}

	.option-button {
		flex: 1;
		padding: 0.5rem;
		border: 1px solid #dbdbdb;
		background-color: #fff;
		color: #4a4a4a;
		font-size: 0.9rem;
		cursor: pointer;
		transition: all 0.2s;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 0.4rem;
	}

	.option-button:first-child {
		border-radius: 4px 0 0 4px;
	}

	.option-button:last-child {
		border-radius: 0 4px 4px 0;
	}

	.option-button:only-child {
		border-radius: 4px;
	}

	.option-button:not(:last-child) {
		border-right: none;
	}

	.option-button:hover {
		background-color: #f5f5f5;
	}

	.draw-prompt {
		text-align: center;
		padding: 2rem 1rem;
	}

	.prompt-text {
		font-size: 0.95rem;
		color: #4a5568;
		margin: 0 0 0.5rem 0;
	}

	.helper-text {
		font-size: 0.9rem;
		color: #4a5568;
		margin: 1rem 0 0 0;
		line-height: 1.6;
		padding: 0.75rem 1rem;
		background: #f7fafc;
		border-radius: 6px;
		border-left: 3px solid #667eea;
		text-align: left;
	}

	.point-count {
		font-size: 0.8rem;
		color: #667eea;
		margin: 0.75rem 0 0 0;
		font-weight: 500;
	}

	.finish-btn {
		margin-top: 1rem;
		background-color: #3273dc;
		color: white;
		border: none;
		border-radius: 4px;
		cursor: pointer;
		padding: 0.5rem 1.2rem;
		font-size: 0.85rem;
		font-weight: 500;
		display: inline-flex;
		align-items: center;
		gap: 0.4rem;
		transition: background-color 0.2s;
	}

	.finish-btn:hover {
		background-color: #2366d1;
	}

	.loading-container {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 2rem;
		gap: 1rem;
	}

	.spinner {
		width: 40px;
		height: 40px;
		border: 3px solid rgba(102, 126, 234, 0.1);
		border-top: 3px solid #667eea;
		border-radius: 50%;
		animation: spin 1s linear infinite;
	}

	.loading-text {
		color: #667eea;
		font-size: 0.9rem;
		font-weight: 500;
		margin: 0;
	}

	@keyframes spin {
		0% { transform: rotate(0deg); }
		100% { transform: rotate(360deg); }
	}

	/* Summary styles matching SummaryView */
	.summary-container {
		background-color: #fff;
		border-radius: 4px;
		border: 1px solid #dbdbdb;
		box-shadow: 0 2px 3px rgba(0, 0, 0, 0.1);
		max-height: 70vh;
		overflow-y: auto;
	}

	.summary-header {
		padding: 12px 16px;
		border-bottom: 1px solid #eee;
		display: flex;
		justify-content: space-between;
		align-items: center;
		position: sticky;
		top: 0;
		background-color: #fff;
		z-index: 2;
	}

	.summary-header h3 {
		margin: 0;
		color: #363636;
		font-size: 1.2rem;
	}

	.total-count {
		font-size: 0.85rem;
		color: #667eea;
		font-weight: 500;
	}

	.summary-content {
		padding: 12px 16px;
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	.feeder-group {
		border: 1px solid #e0e0e0;
		border-radius: 4px;
		overflow: hidden;
	}

	.feeder-header {
		padding: 10px 12px;
		background-color: #eef2ff;
		display: flex;
		align-items: center;
		cursor: pointer;
		transition: background-color 0.2s;
		font-weight: 600;
	}

	.feeder-header:hover {
		background-color: #e0e7ff;
	}

	.feeder-content {
		padding: 4px 0 4px 12px;
		display: flex;
		flex-direction: column;
		gap: 4px;
	}

	.totals-section {
		border-top: 2px solid #395a99;
		margin-top: 8px;
		padding-top: 8px;
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	.totals-label {
		font-weight: 700;
		font-size: 1rem;
		color: #395a99;
		padding: 0 4px;
	}

	.table-item {
		overflow: hidden;
	}

	.table-header {
		padding: 8px 12px;
		background-color: #f5f5f5;
		display: flex;
		align-items: center;
		cursor: pointer;
		transition: background-color 0.2s;
	}

	.table-header:hover {
		background-color: #eaeaea;
	}

	.table-header.nested {
		padding-left: 8px;
	}

	.expand-icon {
		margin-right: 8px;
		width: 16px;
	}

	.table-icon {
		margin-right: 8px;
		color: black;
	}

	.table-name {
		flex-grow: 1;
		font-weight: 500;
	}

	.table-count {
		background-color: #3273dc;
		color: white;
		border-radius: 12px;
		padding: 2px 8px;
		font-size: 0.8rem;
		min-width: 24px;
		text-align: center;
	}

	.kva-badge {
		background-color: #3273dc;
		color: white;
		border-radius: 12px;
		padding: 2px 8px;
		font-size: 0.8rem;
		min-width: 24px;
		text-align: center;
		display: flex;
		align-items: center;
		gap: 4px;
	}

	.total-badge {
		font-weight: bold;
		background-color: #395a99;
	}

	.phase-list {
		padding: 8px 12px;
		background-color: #fff;
	}

	.phase-item {
		display: flex;
		justify-content: space-between;
		padding: 4px 0 4px 32px;
		border-bottom: 1px dashed #eee;
	}

	.phase-item:last-child {
		border-bottom: none;
	}

	.phase-name {
		font-size: 0.9rem;
	}

	.phase-count {
		background-color: #3273dc;
		color: white;
		border-radius: 12px;
		padding: 1px 8px;
		font-size: 0.8rem;
		min-width: 24px;
		text-align: center;
	}

	.phase-indicator {
		padding: 2px 8px;
		border-radius: 4px;
		font-weight: 500;
		min-width: 32px;
		text-align: center;
		display: inline-block;
	}

	.phase-kva-details {
		display: flex;
		align-items: center;
		gap: 6px;
	}

	.kva-value {
		font-weight: 500;
	}

	.kva-percent {
		font-size: 0.8rem;
		color: #666;
	}

	.phase-a { background-color: rgba(152, 32, 19, 0.2); color: #982013; }
	.phase-b { background-color: rgba(25, 149, 12, 0.2); color: #19950c; }
	.phase-c { background-color: rgba(24, 51, 139, 0.2); color: #18338b; }
	.phase-ab { background: linear-gradient(135deg, rgba(152, 32, 19, 0.2) 50%, rgba(25, 149, 12, 0.2) 50%); color: #333; }
	.phase-ac { background: linear-gradient(135deg, rgba(152, 32, 19, 0.2) 50%, rgba(24, 51, 139, 0.2) 50%); color: #333; }
	.phase-bc { background: linear-gradient(135deg, rgba(25, 149, 12, 0.2) 50%, rgba(24, 51, 139, 0.2) 50%); color: #333; }
	.phase-abc { background: linear-gradient(135deg, rgba(152, 32, 19, 0.2) 33%, rgba(25, 149, 12, 0.2) 33%, rgba(25, 149, 12, 0.2) 66%, rgba(24, 51, 139, 0.2) 66%); color: #333; }
	.phase-unknown { background-color: #f0f0f0; color: #666; }

	.empty-state {
		text-align: center;
		padding: 24px 0;
		color: #999;
		font-style: italic;
	}

</style>
